import React, { createContext, FC, useContext, useMemo } from "react";
import { HeaderFetcherArg, HeadersFetcher, Logger, Cookies } from "./types";
import { createVisitFetcher } from "./visitFetcher";

const HeadersFetcherContext = createContext<HeadersFetcher | null>(null);

const defaultHeaders = {
  "Content-Type": "application/json",
};

const addHeader = (
  headers: Record<string, string>,
  key: string,
  value: string | undefined
): void => {
  if (!value) {
    return;
  }
  headers[key] = value;
};

const getCookie = (): Cookies =>
  document?.cookie?.split?.(";")?.reduce?.((acc, cookie) => {
    const [key, value] = cookie.split("=");
    acc[key.trim()] = value;
    return acc;
  }, {}) || {};

const createHeadersFetcher = ({
  visitFetcher,
}: HeaderFetcherArg): HeadersFetcher => {
  return async () => {
    const headers: Record<string, string> = { ...defaultHeaders };

    const visitToken: string = await visitFetcher();
    const cookie = getCookie();
    const jSessionId = cookie.OF1JSESSIONID;
    const featureContext = cookie.FeatureContext;
    addHeader(headers, "X-visit", visitToken);
    addHeader(headers, "X-OF1JSESSIONID", jSessionId);
    addHeader(headers, "EDO-Feature-Context", featureContext);
    return headers;
  };
};

export type HeadersProviderProps = {
  tokenFallback?: string;
  logger?: Logger;
};

const noop = (): void => {};
const defaultLogger = {
  error: noop,
  info: noop,
  message: noop,
  disableLoggers: noop,
  warn: noop,
};

export const HeadersProvider: FC<HeadersProviderProps> = ({
  tokenFallback = "",
  logger = defaultLogger,
  children,
} = {}) => {
  const headersFetcher = useMemo(() => {
    const visitFetcher = createVisitFetcher({
      tokenFallback: tokenFallback ?? "",
      logger,
    });
    return createHeadersFetcher({
      visitFetcher,
    });
  }, [logger, tokenFallback]);
  return (
    <HeadersFetcherContext.Provider value={headersFetcher}>
      {children}
    </HeadersFetcherContext.Provider>
  );
};

export const useHeadersFetcher = (): HeadersFetcher | null => {
  return useContext(HeadersFetcherContext);
};
